// ==========================================
// src/pages/Agradecimento.jsx
// PÁGINA DE AGRADECIMENTO APÓS CONTRATO - V3 CORRIGIDA
// ✅ Busca motorista_id correto da tabela motoristas (não auth.uid)
// ✅ Status 'aceito' compatível com CHECK constraint
// ✅ Salva contrato no Supabase + localStorage
// ✅ Redireciona para /painel
// ==========================================

import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  CheckCircle2, Trophy, Gift, Truck, Star, 
  ArrowRight, Share2, Copy, MessageCircle,
  TrendingUp, Award, Zap, MapPin, Phone,
  ChevronRight, Sparkles, Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/lib/AuthContext';

export default function Agradecimento() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { numeroContrato, carga, motorista } = location.state || {};
  const [copied, setCopied] = useState(false);
  const [showConfetti, setShowConfetti] = useState(true);
  const [processando, setProcessando] = useState(true);

  // ✅ CORRIGIDO: Busca motorista_id correto da tabela motoristas
  useEffect(() => {
    const criarContrato = async () => {
      try {
        if (!carga || !motorista || !user?.id) {
          console.error('❌ Dados incompletos:', { carga, motorista, user });
          setProcessando(false);
          return;
        }

        console.log('🚀 Criando contrato...');
        console.log('Carga:', carga);
        console.log('Motorista:', motorista);
        console.log('User ID (auth):', user.id);

        // 🔥 PASSO 1: Buscar o ID correto do motorista na tabela motoristas
        let motoristaIdReal = null;
        let motoristaDados = null;

        try {
          const { data: motoristaData, error: motoristaError } = await supabase
            .from('motoristas')
            .select('id, nome_completo, email, cnh, placa_caminhao, categoria_cnh')
            .eq('usuario_id', user.id)
            .maybeSingle();

          if (!motoristaError && motoristaData) {
            motoristaIdReal = motoristaData.id; // ✅ ID da tabela motoristas (UUID)
            motoristaDados = motoristaData;
            console.log('✅ Motorista encontrado:', motoristaIdReal);
          } else {
            console.warn('⚠️ Motorista não encontrado na tabela, usando fallback');
          }
        } catch (err) {
          console.warn('⚠️ Erro ao buscar motorista:', err);
        }

        // Se não achou no Supabase, tenta localStorage
        if (!motoristaIdReal) {
          const motoristasLocal = JSON.parse(localStorage.getItem('borafrete_motoristas') || '[]');
          const motoristaLocal = motoristasLocal.find(m => m.usuario_id === user.id || m.email === user.email);
          if (motoristaLocal?.id) {
            motoristaIdReal = motoristaLocal.id;
            console.log('✅ Motorista ID do localStorage:', motoristaIdReal);
          }
        }

        // Fallback final: se não achou ID da tabela, usa auth.uid() (vai dar erro 409, mas tenta)
        const motoristaIdFinal = motoristaIdReal || user.id;
        console.log('🔑 Motorista ID final usado:', motoristaIdFinal);

        // 2. Criar objeto do contrato para Supabase
        const contratoData = {
          carga_id: carga.id,
          motorista_id: motoristaIdFinal,           // ✅ ID correto da tabela motoristas
          empresa_id: carga.empresa_id || null,
          valor_frete: carga.valor_frete || carga.valor || 0,
          taxa_agenciamento: 50.00,
          status: 'aceito',                         // ✅ CORRETO! 'aceito' existe no CHECK
          data_aceite: new Date().toISOString(),
          data_inicio: new Date().toISOString(),
          data_vencimento: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
          forma_pagamento: 'pix',
          motorista_nome: motoristaDados?.nome_completo || motorista.nome || user.nome || 'Motorista',
          motorista_email: user.email || motorista.email,
          motorista_cnh: motoristaDados?.cnh || motorista.cnh || '',
          produto: carga.titulo || 'Carga',
          origem_cidade: carga.origem || carga.origem_cidade || '',
          origem_estado: carga.origem_estado || '',
          destino_cidade: carga.destino || carga.destino_cidade || '',
          destino_estado: carga.destino_estado || '',
          tipo_veiculo: carga.tipo_veiculo || '',
          peso_valor: carga.peso_valor || carga.peso || 0,
          peso_unidade: carga.peso_unidade || 'ton'
        };

        // 3. Salvar no Supabase
        let supabaseContratoId = null;
        try {
          const { data: contratoSupabase, error } = await supabase
            .from('contratos')
            .insert(contratoData)
            .select()
            .single();

          if (error) {
            console.error('❌ Erro Supabase:', error);
            // Se der erro 409 (FK), mostra toast específico
            if (error.code === '23503' || error.message?.includes('foreign key')) {
              toast.error('Erro: Motorista não cadastrado corretamente. Contate o suporte.');
            } else {
              toast.warning('Contrato salvo localmente. Sincronização pendente.');
            }
          } else {
            supabaseContratoId = contratoSupabase.id;
            console.log('✅ Contrato salvo no Supabase:', supabaseContratoId);
            toast.success('🎉 Contrato registrado no sistema!');
          }
        } catch (supabaseError) {
          console.error('❌ Erro Supabase:', supabaseError);
          toast.warning('Salvando contrato localmente...');
        }

        // 4. Salvar no localStorage (backup/fallback)
        const contratoLocal = {
          id: numeroContrato || `CONT_${Date.now()}`,
          supabase_id: supabaseContratoId,
          carga_id: carga.id,
          motorista_id: motoristaIdFinal,           // ✅ ID correto
          motorista_email: user.email || motorista.email,
          motorista_nome: motoristaDados?.nome_completo || motorista.nome || user.nome || 'Motorista',
          origem: carga.origem || carga.origem_cidade || '',
          origem_cidade: carga.origem_cidade || carga.origem || '',
          origem_estado: carga.origem_estado || '',
          destino: carga.destino || carga.destino_cidade || '',
          destino_cidade: carga.destino_cidade || carga.destino || '',
          destino_estado: carga.destino_estado || '',
          valor: carga.valor || carga.valor_frete || 0,
          valor_frete: carga.valor_frete || carga.valor || 0,
          peso: carga.peso || '',
          tipo_veiculo: carga.tipo_veiculo || carga.tipoVeiculo || '',
          distancia: carga.distancia || carga.km || '',
          produto: carga.titulo || carga.produto || carga.tipo_carga || 'Carga',
          status: 'aceito',                         // ✅ 'aceito'
          etapa: 'contrato_assinado',
          data_inicio: new Date().toISOString(),
          data_assinatura: new Date().toISOString(),
          data_vencimento: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
          progresso_entrega: 0,
          km_percorridos: 0,
          pago: true,
          assinado: true,
          ativo: true
        };

        const contratos = JSON.parse(localStorage.getItem('borafrete_contratos') || '[]');
        const existe = contratos.find(c => c.carga_id === carga.id && c.motorista_id === motoristaIdFinal);

        if (existe) {
          console.log('⚠️ Contrato já existe no localStorage:', existe.id);
        } else {
          contratos.push(contratoLocal);
          localStorage.setItem('borafrete_contratos', JSON.stringify(contratos));
          console.log('✅ Contrato salvo no localStorage:', contratoLocal.id);
        }

        // 5. Atualizar carga no localStorage
        const cargas = JSON.parse(localStorage.getItem('borafrete_cargas') || '[]');
        const cargasAtualizadas = cargas.map(c => {
          if (c.id === carga.id) {
            return {
              ...c,
              status: 'aceita',
              motorista_id: motoristaIdFinal,        // ✅ ID correto
              motorista_nome: contratoLocal.motorista_nome,
              data_aceite: new Date().toISOString()
            };
          }
          return c;
        });
        localStorage.setItem('borafrete_cargas', JSON.stringify(cargasAtualizadas));

        // 6. Criar notificação
        const notificacoes = JSON.parse(localStorage.getItem('borafrete_notificacoes') || '[]');
        notificacoes.unshift({
          id: `NOTIF_${Date.now()}`,
          tipo: 'contrato_assinado',
          motorista_id: motoristaIdFinal,            // ✅ ID correto
          titulo: '🎉 Contrato Assinado!',
          mensagem: `Você assinou o contrato para ${carga.titulo || 'Carga'}`,
          carga_id: carga.id,
          contrato_id: contratoLocal.id,
          lida: false,
          data: new Date().toISOString()
        });
        localStorage.setItem('borafrete_notificacoes', JSON.stringify(notificacoes));

        if (!supabaseContratoId) {
          toast.info('⚠️ Contrato salvo localmente. Verifique seu cadastro no admin.');
        }

        setProcessando(false);

      } catch (error) {
        console.error('❌ Erro ao criar contrato:', error);
        toast.error('Erro ao registrar contrato');
        setProcessando(false);
      }
    };

    const timer = setTimeout(criarContrato, 500);
    return () => clearTimeout(timer);
  }, [carga, motorista, numeroContrato, user]);

  useEffect(() => {
    const timer = setTimeout(() => setShowConfetti(false), 5000);
    return () => clearTimeout(timer);
  }, []);

  const handleCopyCode = () => {
    navigator.clipboard.writeText(numeroContrato);
    setCopied(true);
    toast.success('Código copiado!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Contratei uma carga no BoraFrete!',
          text: `Acabei de contratar a carga ${carga?.titulo}. Contrato: ${numeroContrato}`,
          url: window.location.origin
        });
      } catch (err) {
        console.log('Erro ao compartilhar:', err);
      }
    }
  };

  const handleWhatsAppAtendente = () => {
    const mensagem = encodeURIComponent(
      `🚛 *NOVA CARGA CONTRATADA - BORAFRETE*\n\n` +
      `👤 *Motorista:* ${motorista?.nome || 'Não informado'}\n` +
      `📋 *Contrato:* ${numeroContrato || 'N/A'}\n\n` +
      `*DETALHES DA CARGA:*\n` +
      `📦 ${carga?.titulo || 'Carga'}\n` +
      `📍 Origem: ${carga?.origem || 'N/A'}\n` +
      `📍 Destino: ${carga?.destino || 'N/A'}\n` +
      `💰 Valor: R$ ${carga?.valor || 'N/A'}\n\n` +
      `✅ Contrato assinado com sucesso!`
    );

    window.open(`https://wa.me/5565981181997?text=${mensagem}`, '_blank');
  };

  const irParaPainel = () => {
    navigate('/painel', { 
      replace: true,
      state: { refresh: Date.now() }
    });
  };

  if (!numeroContrato) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <Card className="p-8 text-center">
          <h2 className="text-2xl font-bold mb-4">Nenhum contrato encontrado</h2>
          <Button onClick={() => navigate('/buscar-cargas')} className="bg-green-600">
            Buscar Cargas
          </Button>
        </Card>
      </div>
    );
  }

  if (processando) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <Card className="p-8 text-center">
          <Loader2 className="w-12 h-12 animate-spin text-green-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white">Registrando contrato...</h2>
          <p className="text-slate-400">Preparando sua carga no painel</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 overflow-x-hidden">
      <div className="relative min-h-screen flex items-center justify-center">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('https://kimi-web-img.moonshot.cn/img/img.freepik.com/2dcd775a2485f515b6ccb3fff17adadff0d22140.jpg')`,
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 via-slate-950/40 to-slate-950/20" />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950/60 via-transparent to-slate-950/60" />
        </div>

        {showConfetti && (
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            {[...Array(20)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute w-3 h-3 rounded-full"
                style={{
                  background: ['#22c55e', '#eab308', '#3b82f6', '#f97316'][i % 4],
                  left: `${Math.random() * 100}%`,
                  top: -20,
                }}
                animate={{
                  y: [0, window.innerHeight + 100],
                  x: [0, (Math.random() - 0.5) * 200],
                  rotate: [0, 360],
                }}
                transition={{
                  duration: 3 + Math.random() * 2,
                  repeat: Infinity,
                  delay: Math.random() * 2,
                  ease: "linear"
                }}
              />
            ))}
          </div>
        )}

        <div className="relative z-10 w-full max-w-6xl mx-auto px-4 py-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">

            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="text-white space-y-8"
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.3, type: "spring" }}
              >
                <Badge className="bg-green-500/20 text-green-400 border-green-500/50 px-4 py-2 text-lg font-bold backdrop-blur-sm">
                  <CheckCircle2 className="w-5 h-5 mr-2 inline" />
                  CONTRATO ASSINADO COM SUCESSO!
                </Badge>
              </motion.div>

              <div className="space-y-4">
                <h1 className="text-5xl lg:text-7xl font-black leading-tight drop-shadow-lg">
                  <span className="bg-gradient-to-r from-green-400 via-emerald-400 to-teal-400 bg-clip-text text-transparent">
                    É SÓ
                  </span>
                  <br />
                  <span className="text-white drop-shadow-md">BoraFrete!</span>
                </h1>
                <p className="text-xl lg:text-2xl text-white font-light drop-shadow-md">
                  Parabéns, <span className="text-green-400 font-semibold">{motorista?.nome || motorista}</span>!
                  <br />
                  Você acabou de dar um grande passo na sua carreira.
                </p>
              </div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
              >
                <Card className="bg-white/10 backdrop-blur-md border-white/20 overflow-hidden">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-slate-200 text-sm mb-1">Código do Contrato</p>
                        <p className="text-3xl font-mono font-bold text-white tracking-wider">
                          {numeroContrato}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={handleCopyCode}
                          className="text-white hover:bg-white/20"
                        >
                          {copied ? <CheckCircle2 className="text-green-400" /> : <Copy />}
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={handleShare}
                          className="text-white hover:bg-white/20"
                        >
                          <Share2 />
                        </Button>
                      </div>
                    </div>
                    <div className="mt-4 pt-4 border-t border-white/10">
                      <div className="flex items-center gap-4 text-sm text-slate-200">
                        <span className="flex items-center gap-1">
                          <Truck className="w-4 h-4 text-green-400" />
                          {carga?.titulo}
                        </span>
                        <span className="flex items-center gap-1">
                          <MapPin className="w-4 h-4 text-blue-400" />
                          {carga?.origem} → {carga?.destino}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
              >
                <Button 
                  onClick={handleWhatsAppAtendente}
                  className="w-full bg-green-500 hover:bg-green-600 text-white px-8 py-6 text-lg font-bold rounded-full shadow-lg shadow-green-500/25 transition-all hover:scale-105 flex items-center justify-center gap-3 mb-4"
                >
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  Falar com Atendente no WhatsApp
                </Button>

                <p className="text-sm text-slate-200 text-center mb-4">
                  📱 (65) 98118-1997 • Envie os dados da sua carga
                </p>
              </motion.div>

              <div className="flex flex-wrap gap-4">
                <Button 
                  onClick={irParaPainel}
                  className="bg-green-500 hover:bg-green-600 text-white px-8 py-6 text-lg font-bold rounded-full shadow-lg shadow-green-500/25 transition-all hover:scale-105"
                >
                  Ir para Meu Painel
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => navigate('/buscar-cargas')}
                  className="border-white/50 text-white hover:bg-white/20 px-8 py-6 text-lg rounded-full backdrop-blur-sm"
                >
                  Buscar Mais Cargas
                </Button>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="space-y-6"
            >
              <Card className="bg-white/10 backdrop-blur-md border-white/20 overflow-hidden relative">
                <div className="absolute top-0 right-0 w-32 h-32 bg-green-500/20 rounded-full blur-3xl" />
                <CardContent className="p-6 relative">
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 bg-green-500 rounded-2xl flex items-center justify-center shadow-lg shadow-green-500/30">
                      <Zap className="w-7 h-7 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-white mb-2">
                        Sem Mensalidade!
                      </h3>
                      <p className="text-slate-200">
                        Você só paga quando carregar. Nada de taxas fixas ou assinaturas. 
                        Só R$ 50,00 por carga contratada.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white/10 backdrop-blur-md border-white/20 overflow-hidden relative">
                <div className="absolute top-0 right-0 w-32 h-32 bg-amber-500/20 rounded-full blur-3xl" />
                <CardContent className="p-6 relative">
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 bg-amber-500 rounded-2xl flex items-center justify-center shadow-lg shadow-amber-500/30">
                      <Trophy className="w-7 h-7 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-white mb-2">
                        Ganhe Prêmios Todo Mês!
                      </h3>
                      <p className="text-slate-200">
                        Quanto mais cargas você completar, mais pontos acumula. 
                        Troque por prêmios em dinheiro, combustível e muito mais!
                      </p>
                      <div className="mt-3 flex items-center gap-2 text-amber-400">
                        <Sparkles className="w-4 h-4" />
                        <span className="text-sm font-semibold">Programa de Fidelidade Ativo</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white/10 backdrop-blur-md border-white/20 overflow-hidden relative">
                <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500/20 rounded-full blur-3xl" />
                <CardContent className="p-6 relative">
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 bg-blue-500 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-500/30">
                      <TrendingUp className="w-7 h-7 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-white mb-2">
                        Suba de Nível!
                      </h3>
                      <p className="text-slate-200">
                        Cada carga completa conta pontos para seu nível. 
                        Motoristas Ouro e Diamante têm acesso a cargas exclusivas!
                      </p>
                      <div className="mt-3 flex items-center gap-2">
                        <Badge className="bg-slate-700/80 text-slate-200">Bronze</Badge>
                        <ChevronRight className="w-4 h-4 text-slate-400" />
                        <Badge className="bg-slate-600/80 text-slate-200">Prata</Badge>
                        <ChevronRight className="w-4 h-4 text-slate-400" />
                        <Badge className="bg-amber-600 text-white">Ouro</Badge>
                        <ChevronRight className="w-4 h-4 text-slate-400" />
                        <Badge className="bg-blue-600 text-white">Diamante</Badge>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </div>

      <section className="py-20 px-4 bg-slate-950">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-4">
              Como funciona o <span className="text-green-400">Programa de Prêmios</span>?
            </h2>
            <p className="text-xl text-slate-400 max-w-2xl mx-auto">
              Quanto mais você rodar, mais ganha. Simples assim.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[...Array(3)].map((_, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
              >
                <Card className="bg-slate-900/50 border-slate-800 h-full hover:border-slate-700 transition-colors group">
                  <CardContent className="p-8 text-center">
                    <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-green-500/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Truck className="w-10 h-10 text-green-400" />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-3">Complete Cargas</h3>
                    <p className="text-slate-400 leading-relaxed">Cada carga entregue conta pontos para seu nível.</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 px-4 bg-gradient-to-t from-slate-950 to-slate-900">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl lg:text-6xl font-black text-white mb-6">
              Precisa de <span className="text-green-400">ajuda</span>?
            </h2>
            <Button 
              onClick={handleWhatsAppAtendente}
              className="bg-green-500 hover:bg-green-600 text-white px-10 py-7 text-xl font-bold rounded-full shadow-lg shadow-green-500/25 transition-all hover:scale-105"
            >
              <svg className="w-6 h-6 mr-2" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              Falar no WhatsApp
            </Button>
          </motion.div>
        </div>
      </section>

      <footer className="py-8 px-4 bg-slate-950 border-t border-slate-800">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 bg-gradient-to-br from-green-400 to-emerald-600 rounded-lg flex items-center justify-center">
              <Truck className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-bold text-white">BoraFrete</span>
          </div>
          <p className="text-slate-500 text-sm">
            © 2026 BoraFrete. Todos os direitos reservados.
          </p>
          <Button 
            variant="ghost" 
            size="sm" 
            className="text-slate-400 hover:text-white"
            onClick={handleWhatsAppAtendente}
          >
            <Phone className="w-4 h-4 mr-2" />
            (65) 98118-1997
          </Button>
        </div>
      </footer>
    </div>
  );
}